/**
 * Historian Reports API client
 *
 * Thin client for the canned-report endpoints added in djin-cloud#60.
 * Mirrors the shape of `historianApi.ts` so error-handling and timeout
 * semantics stay consistent across the historian surface.
 */

import api from './api';
import { extractHistorianError, HistorianErrorBody } from './historianApi';

// Reports can legitimately take longer than the 10s axios default on a
// wide monthly range against raw telemetry on POD (Azure Flexible Server
// path, no continuous aggregates). Match the historian query timeout.
const REPORTS_TIMEOUT_MS = 60_000;

/** Shared report metadata returned by all three endpoints. */
export interface ReportMetadata {
  report: 'interval' | 'daily' | 'monthly';
  site_id: string;
  tz: string;
  power_metric: string;
  start_utc: string;
  end_utc: string;
  row_count: number;
  query_time_ms: number;
  integral_formula: string;
}

export interface IntervalReportRow {
  bucket_start: string;
  peak_kw: number | null;
  avg_kw: number | null;
  kwh: number | null;
  kvar: number | null;
  pf: number | null;
  fuel_rate_mmbtu_h: number | null;
  energy_mmbtu: number | null;
}

export interface DailyReportRow {
  day: string;
  daily_peak_kw: number | null;
  daily_kwh: number | null;
  daily_fuel_mmbtu: number | null;
}

export interface MonthlyReportRow {
  month: string;
  monthly_peak_kw: number | null;
  monthly_kwh: number | null;
  monthly_fuel_mmbtu: number | null;
}

export interface IntervalReportRequest {
  siteId: string;
  deviceIds?: string[];
  powerMetric?: string;
  day: string;
  tz: string;
}
export interface DailyReportRequest {
  siteId: string;
  deviceIds?: string[];
  powerMetric?: string;
  month: string;
  tz: string;
}
export interface MonthlyReportRequest {
  siteId: string;
  deviceIds?: string[];
  powerMetric?: string;
  startMonth: string;
  endMonth: string;
  tz: string;
}

export interface ReportResult<Row> {
  columns: string[];
  rows: Row[];
  metadata: ReportMetadata;
}

export async function runIntervalReport(
  req: IntervalReportRequest,
): Promise<ReportResult<IntervalReportRow>> {
  const res = await api.post<{ success: boolean; data: ReportResult<IntervalReportRow> }>(
    '/v1/historian/reports/interval',
    req,
    { timeout: REPORTS_TIMEOUT_MS },
  );
  return res.data.data;
}

export async function runDailyReport(
  req: DailyReportRequest,
): Promise<ReportResult<DailyReportRow>> {
  const res = await api.post<{ success: boolean; data: ReportResult<DailyReportRow> }>(
    '/v1/historian/reports/daily',
    req,
    { timeout: REPORTS_TIMEOUT_MS },
  );
  return res.data.data;
}

export async function runMonthlyReport(
  req: MonthlyReportRequest,
): Promise<ReportResult<MonthlyReportRow>> {
  const res = await api.post<{ success: boolean; data: ReportResult<MonthlyReportRow> }>(
    '/v1/historian/reports/monthly',
    req,
    { timeout: REPORTS_TIMEOUT_MS },
  );
  return res.data.data;
}

/**
 * Download a report as a CSV attachment via the backend streaming path.
 * Uses axios with `responseType: 'blob'` so the browser receives the stream
 * unchanged, then triggers a download via an object URL plus synthetic
 * anchor click. Server sets Content-Disposition, parsed here for filename.
 *
 * Error path: axios wraps non-2xx responses as errors whose `response.data`
 * is a Blob (because of `responseType: 'blob'`), not a JSON object. Left
 * unhandled, callers' `extractHistorianError` silently falls back to
 * `{ code: 'network', message: 'Unknown error' }` — which is the wrong
 * diagnosis when the server actually returned a structured
 * `{ success: false, error: { code, message, hint } }` body (typical for
 * 403 forbidden / 504 query_timeout / 400 invalid_*). We decode the blob
 * as text, parse as JSON, and rethrow with the shape
 * `extractHistorianError` expects so the UI surfaces the real error.
 */
export async function downloadReportCsv(
  endpoint: 'interval' | 'daily' | 'monthly',
  body: IntervalReportRequest | DailyReportRequest | MonthlyReportRequest,
  fallbackFilename: string,
): Promise<void> {
  let res;
  try {
    res = await api.post<Blob>(
      `/v1/historian/reports/${endpoint}`,
      { ...body, format: 'csv' },
      { responseType: 'blob', timeout: REPORTS_TIMEOUT_MS },
    );
  } catch (err) {
    const resp = (err as { response?: { data?: unknown; status?: number } }).response;
    if (resp?.data instanceof Blob) {
      const text = await resp.data.text();
      try {
        const parsed = JSON.parse(text);
        // Rethrow with the shape extractHistorianError expects:
        // `{ response: { data: { error: { code, message, hint } } } }`.
        throw { response: { data: parsed, status: resp.status } };
      } catch (parseErr) {
        // If rethrow target was our synthesized object, pass it through.
        if (parseErr && typeof parseErr === 'object' && 'response' in parseErr) throw parseErr;
        // Otherwise the body wasn't JSON — let the original axios error bubble.
      }
    }
    throw err;
  }

  const blob = res.data;
  const disposition = res.headers['content-disposition'] ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export { extractHistorianError };
export type { HistorianErrorBody };
